import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Calendar, CheckCircle2, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useQueryClient } from "@tanstack/react-query";
import {
  useRecordAdjustmentCsv,
  type AdjustmentCsvLineItem,
  type RecordAdjustmentCsvSuccess,
} from "@/hooks/useRecordAdjustmentCsv";
import { useInventoryLedgerSources } from "@/hooks/useInventoryLedgerSources";
import { Badge } from "@/components/ui/badge";
import type { PoolType } from "@/hooks/useProductPools";
import {
  mergeThousandsIfLastColumn,
  normalizeInventoryCsvRow,
  parseInventoryQuantity,
} from "@/utils/csvQuantity";

const POOL_LABEL: Record<PoolType, string> = {
  received: "Received pool",
  platform: "Platform pool",
};

type ColumnRole = "sku" | "asin" | "upc" | "ean" | "gtin" | "barcode" | "quantity" | "ignore";

const COLUMN_ROLE_OPTIONS: { value: ColumnRole; label: string }[] = [
  { value: "sku", label: "SKU (identifier)" },
  { value: "asin", label: "ASIN (identifier)" },
  { value: "upc", label: "UPC (identifier)" },
  { value: "ean", label: "EAN (identifier)" },
  { value: "gtin", label: "GTIN (identifier)" },
  { value: "barcode", label: "Barcode (identifier)" },
  { value: "quantity", label: "Quantity" },
  { value: "ignore", label: "Ignore" },
];

const IDENTIFIER_ROLE_VALUES = new Set<ColumnRole>(["sku", "asin", "upc", "ean", "gtin", "barcode"]);

function guessColumnRole(header: string): ColumnRole {
  const h = header.toLowerCase().trim();
  if (["sku", "seller-sku", "seller sku", "product_sku", "item_sku", "item-sku", "msku", "merchant_sku", "merchant-sku"].includes(h)) return "sku";
  if (h === "asin" || h === "fnsku") return "asin";
  if (h === "upc") return "upc";
  if (h === "ean") return "ean";
  if (h === "gtin") return "gtin";
  if (h === "barcode") return "barcode";
  if (h === "quantity" || h === "qty" || h === "units" || h === "count") return "quantity";
  return "ignore";
}

function splitCsvLine(line: string): string[] {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;
  for (const char of line) {
    if (char === '"') { inQuotes = !inQuotes; }
    else if (char === "," && !inQuotes) { values.push(current.trim().replace(/^"|"$/g, "")); current = ""; }
    else { current += char; }
  }
  values.push(current.trim().replace(/^"|"$/g, ""));
  return values;
}

type Step = "configure" | "map-columns" | "success";

interface Props {
  organisationId: string;
  open: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
}

const ReceivedInventoryCsvUpload: React.FC<Props> = ({ organisationId, open, onClose, onUploadSuccess }) => {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("configure");
  const [file, setFile] = useState<File | null>(null);
  const [rawContent, setRawContent] = useState<string>("");
  const [date, setDate] = useState<Date>(new Date());
  const [calOpen, setCalOpen] = useState(false);

  // Column mapping state
  const [headers, setHeaders] = useState<string[]>([]);
  const [columnRoles, setColumnRoles] = useState<ColumnRole[]>([]);

  const [successData, setSuccessData] = useState<RecordAdjustmentCsvSuccess | null>(null);
  const [ledgerSource, setLedgerSource] = useState<string>("");

  const { upload } = useRecordAdjustmentCsv();
  const { data: sources = [], isLoading: sourcesLoading } = useInventoryLedgerSources(organisationId, open);

  const selectableSources = useMemo(
    () => sources.filter((s) => s.source !== "manual_received"),
    [sources],
  );

  useEffect(() => {
    if (!open) return;
    if (selectableSources.length === 0) return;
    if (!ledgerSource || ledgerSource === "manual_received") {
      setLedgerSource(selectableSources[0].source);
    }
  }, [open, selectableSources, ledgerSource]);

  const selectedMeta = selectableSources.find((s) => s.source === ledgerSource);

  const effectiveDate = format(date, "yyyy-MM-dd");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setHeaders([]);
    setColumnRoles([]);
  };

  const handleContinueToMapping = async () => {
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) {
      toast.error("File is empty");
      return;
    }
    const firstLine = lines[0] ?? "";
    const hasHeader = /[a-zA-Z]/.test(firstLine.split(",")[0] ?? "");
    if (!hasHeader) {
      toast.error("CSV must have a header row");
      return;
    }
    const detectedHeaders = splitCsvLine(firstLine);
    const detectedRoles = detectedHeaders.map((h) => guessColumnRole(h));
    setRawContent(text);
    setHeaders(detectedHeaders);
    setColumnRoles(detectedRoles);
    setStep("map-columns");
  };

  const identifierColIndex = columnRoles.findIndex((r) => IDENTIFIER_ROLE_VALUES.has(r));
  const quantityColIndex = columnRoles.indexOf("quantity");
  const mappingValid = identifierColIndex !== -1 && quantityColIndex !== -1;

  const buildLineItems = (): AdjustmentCsvLineItem[] => {
    const identifierType = columnRoles[identifierColIndex] as string;
    const lines = rawContent.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).slice(1);
    const items: AdjustmentCsvLineItem[] = [];
    const seen = new Set<string>();
    for (const line of lines) {
      const values = splitCsvLine(line);
      const row = normalizeInventoryCsvRow(values, columnRoles.length, quantityColIndex, {
        allowNegative: true,
      });
      const raw = (row[identifierColIndex] ?? "").trim();
      if (!raw) continue;
      const quantityRaw = mergeThousandsIfLastColumn(
        row,
        quantityColIndex,
        columnRoles.length,
        { allowNegative: true },
      );
      const qty = parseInventoryQuantity(quantityRaw, { allowNegative: true });
      if (qty === null || qty === 0) continue;
      const key = `${identifierType}|${raw.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      items.push({ identifier_type: identifierType, identifier_value: raw, quantity: qty });
    }
    return items;
  };

  const handleConfirmMapping = async () => {
    if (!mappingValid || !file) return;
    const items = buildLineItems();
    if (items.length === 0) {
      toast.error("No valid rows found with the selected column mapping.");
      return;
    }
    try {
      const result = await upload.mutateAsync({
        organisation_id: organisationId,
        source: ledgerSource,
        effective_date: effectiveDate,
        filename: file.name,
        line_items: items,
      });
      setSuccessData(result as RecordAdjustmentCsvSuccess);
      setStep("success");
      if (result.skipped_unmapped > 0) {
        toast.warning(
          `${result.skipped_unmapped} row${result.skipped_unmapped === 1 ? "" : "s"} were skipped because no matching product mapping was found.`,
        );
      }
      queryClient.invalidateQueries({ queryKey: ["adjustment-deltas", organisationId] });
      queryClient.invalidateQueries({ queryKey: ["inventory-audit-log", organisationId] });
      onUploadSuccess?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    }
  };

  const handleClose = () => {
    setStep("configure");
    setFile(null);
    setRawContent("");
    setHeaders([]);
    setColumnRoles([]);
    setSuccessData(null);
    setLedgerSource("");
    if (fileRef.current) fileRef.current.value = "";
    onClose();
  };

  const isSubmitting = upload.isPending;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleClose(); }}>
      <DialogContent className="max-w-lg">

        {step === "configure" && (
          <>
            <DialogHeader>
              <DialogTitle>Upload inventory adjustment</DialogTitle>
              <DialogDescription>
                Upload a CSV file to record inventory adjustments for your products.
                Each row upserts the snapshot for the selected date and appends an ADJUSTMENT event.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Inventory source</Label>
                {sourcesLoading ? (
                  <p className="text-sm text-muted-foreground">Loading sources…</p>
                ) : selectableSources.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No sources available.</p>
                ) : (
                  <Select value={ledgerSource} onValueChange={setLedgerSource}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectableSources.map((s) => (
                        <SelectItem key={s.source} value={s.source}>
                          {s.display_label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedMeta?.origin_kind === "live" && (
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline" className="text-amber-800 border-amber-300 bg-amber-50">
                      Live source
                    </Badge>
                    <span>Adjustments expire when the next live inventory sync completes.</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Effective date</Label>
                <Popover open={calOpen} onOpenChange={setCalOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("w-full justify-start text-left font-normal", !date && "text-muted-foreground")}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {format(date, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={date}
                      onSelect={(d) => { if (d) { setDate(d); setCalOpen(false); } }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>CSV file</Label>
                <div
                  className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  {file ? (
                    <p className="text-sm font-medium">{file.name}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">Click to select a CSV file</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Must include an identifier column and a Quantity column (positive or negative integers)
                  </p>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleContinueToMapping} disabled={!file || !ledgerSource}>
                Continue
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "map-columns" && (
          <>
            <DialogHeader>
              <DialogTitle>Confirm column mapping</DialogTitle>
              <DialogDescription>
                We detected the following columns. Assign each one a role or mark it as ignored.
                Exactly one column must be the identifier and one must be Quantity.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-2 max-h-72 overflow-y-auto pr-1">
              {headers.map((header, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="flex-1 text-sm font-medium truncate" title={header}>{header}</span>
                  <Select
                    value={columnRoles[i] ?? "ignore"}
                    onValueChange={(val) => {
                      const updated = [...columnRoles];
                      updated[i] = val as ColumnRole;
                      setColumnRoles(updated);
                    }}
                  >
                    <SelectTrigger className="w-44 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COLUMN_ROLE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {!mappingValid && (
              <p className="text-xs text-destructive">
                {identifierColIndex === -1
                  ? "Assign one column as an identifier (SKU, ASIN, UPC…)."
                  : "Assign one column as Quantity."}
              </p>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("configure")} disabled={isSubmitting}>
                Go back
              </Button>
              <Button onClick={handleConfirmMapping} disabled={!mappingValid || isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Import
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "success" && successData && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Import complete
              </DialogTitle>
            </DialogHeader>

            <div className="rounded-md border bg-muted/30 p-4 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Events written</span>
                <span className="font-medium">{successData.events_written}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Affected products</span>
                <span className="font-medium">{successData.affected_products}</span>
              </div>
              {successData.skipped_unmapped > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Skipped (no mapping)</span>
                  <span className="font-medium text-yellow-600">{successData.skipped_unmapped}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Source</span>
                <span className="font-medium">{successData.source}</span>
              </div>
              {successData.pool_type && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pool</span>
                  <span className="font-medium">{POOL_LABEL[successData.pool_type]}</span>
                </div>
              )}
              {successData.transient && (
                <p className="text-xs text-amber-800 pt-1">
                  Live-source adjustments are transient and clear on the next sync.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default ReceivedInventoryCsvUpload;
